// ─────────────────────────────────────────────────────────────────────────────
//  Everything this app keeps on the device.
//
//  Three separate concerns, all in localStorage:
//    • drafts    — games you are writing, autosaved as you type
//    • rooms     — rooms you host, and the token that proves it
//    • prefs     — your interface language, and your player id per room
//
//  Every read is defensive: a browser in private mode, a full quota, or a
//  hand-edited value must degrade to "no data", never to a crashed page.
// ─────────────────────────────────────────────────────────────────────────────

import type { Pack } from "@shared/pack";
import { emptyPack, parsePack } from "@shared/pack";
import { isLanguage } from "@shared/i18n";
import type { Language } from "@shared/types";

const K = {
  drafts: "jeopardy.drafts.v2",
  rooms: "jeopardy.rooms.v2",
  language: "jeopardy.language",
  player: (code: string) => `jeopardy.player.${code}`,
} as const;

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    // Quota exceeded or storage disabled. The caller decides whether to warn.
    return false;
  }
}

function remove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* nothing we can do */
  }
}

// ───────────────────────────── drafts ─────────────────────────────

export interface Draft {
  id: string;
  updatedAt: number;
  pack: Pack;
}

export function listDrafts(): Draft[] {
  const raw = readJson<unknown[]>(K.drafts, []);
  if (!Array.isArray(raw)) return [];
  return raw
    .map((d) => {
      const rec = (d ?? {}) as Record<string, unknown>;
      if (typeof rec.id !== "string") return null;
      return {
        id: rec.id,
        updatedAt: Number(rec.updatedAt) || 0,
        pack: parsePack(rec.pack).pack,
      };
    })
    .filter((d): d is Draft => d !== null)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export function getDraft(id: string): Draft | null {
  return listDrafts().find((d) => d.id === id) ?? null;
}

/** Upsert. Returns false if the write failed (quota / private mode). */
export function saveDraft(draft: Draft): boolean {
  const all = listDrafts().filter((d) => d.id !== draft.id);
  all.unshift(draft);
  // Keep the list bounded so a long-time user's storage cannot creep upward.
  return writeJson(K.drafts, all.slice(0, 40));
}

export function deleteDraft(id: string): void {
  writeJson(K.drafts, listDrafts().filter((d) => d.id !== id));
}

export function newDraft(pack: Pack = emptyPack()): Draft {
  return { id: crypto.randomUUID(), updatedAt: Date.now(), pack };
}

// ───────────────────────── hosted rooms ─────────────────────────

export interface HostedRoom {
  code: string;
  title: string;
  hostToken: string;
  createdAt: number;
}

export function listHostedRooms(): HostedRoom[] {
  const raw = readJson<unknown[]>(K.rooms, []);
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r) => {
      const rec = (r ?? {}) as Record<string, unknown>;
      if (typeof rec.code !== "string" || typeof rec.hostToken !== "string") return null;
      return {
        code: rec.code,
        hostToken: rec.hostToken,
        title: typeof rec.title === "string" ? rec.title : "Jeopardy",
        createdAt: Number(rec.createdAt) || 0,
      };
    })
    .filter((r): r is HostedRoom => r !== null)
    .sort((a, b) => b.createdAt - a.createdAt);
}

export function rememberHostedRoom(room: HostedRoom): void {
  const all = listHostedRooms().filter((r) => r.code !== room.code);
  all.unshift(room);
  writeJson(K.rooms, all.slice(0, 25));
}

export function forgetHostedRoom(code: string): void {
  writeJson(K.rooms, listHostedRooms().filter((r) => r.code !== code));
}

export function hostTokenFor(code: string): string | null {
  return listHostedRooms().find((r) => r.code === code)?.hostToken ?? null;
}

// ───────────────────────── player identity ─────────────────────────

export function getPlayerId(code: string): string | null {
  try {
    return localStorage.getItem(K.player(code));
  } catch {
    return null;
  }
}

export function setPlayerId(code: string, id: string): void {
  try {
    localStorage.setItem(K.player(code), id);
  } catch {
    /* the player just loses rejoin-on-refresh */
  }
}

export function clearPlayerId(code: string): void {
  remove(K.player(code));
}

// ───────────────────────── language preference ─────────────────────────

export function getStoredLanguage(): Language | null {
  try {
    const v = localStorage.getItem(K.language);
    return isLanguage(v) ? v : null;
  } catch {
    return null;
  }
}

export function setStoredLanguage(lang: Language): void {
  try {
    localStorage.setItem(K.language, lang);
  } catch {
    /* preference just will not persist */
  }
}

/** Best guess before we know anything: what the browser is set to. */
export function browserLanguage(): Language {
  const nav = typeof navigator !== "undefined" ? navigator.language ?? "" : "";
  return nav.toLowerCase().startsWith("de") ? "de" : "en";
}
